import { useEffect, useState } from 'react';
import { useAlert } from 'react-alert';
import { useDispatch, useSelector } from 'react-redux';
import classnames from 'classnames';
import { format } from 'date-fns';
import I18n from 'i18n-js';
import { object } from 'prop-types';
import querystringify from 'querystringify';

import { connectWithDiscord } from '@lootboy/lib/actions/AuthActions';
import { fetchActiveSubscriptionIfNeeded } from '@lootboy/lib/actions/SubscriptionActions';
import {
  claimMonthlyDiamondBonus,
  fetchUserIfNeeded,
} from '@lootboy/lib/actions/UserActions';
import { getDiscordAuthCodeUrl } from '@lootboy/lib/api/auth';
import useFetchSelector from '@lootboy/lib/hooks/useFetchSelector';

import AlertComponent from '~/components/Alert';
import ButtonPlainPromised from '~/components/ButtonPlainPromised';
import PopupClassic from '~/components/PopupClassic';
import { effectsClassNames } from '~/components/PopupPlain';
import PopupsSection from '~/components/SubscriptionOverview/NewView/PopupsSection';
import SectionInfo from '~/components/SubscriptionOverview/NewView/SectionInfo';
import Content from '~/components/SubscriptionOverview/NewView/SectionInfo/Content';
import Title from '~/components/SubscriptionOverview/NewView/SectionInfo/Title';
import SectionTitle from '~/components/SubscriptionOverview/NewView/SectionTitle';
import SubscriptionCloseButton from '~/components/SubscriptionOverview/NewView/SubscriptionCloseButton';

import useBooleanState from '~/hooks/useBooleanState';
import {
  authSelector,
  evalIsUserSubscribed,
  subscriptionSelector,
  userSelector,
} from '~/redux/selectors';
import { SEASON_TICKET_PATH, SEASON_TICKET_SUCCESS_PATH } from '~/routes/paths';
import { useDateFormat } from '~/utils';

import ticket from '~/assets/images/currencies/1 Ticket Single.png';
import diamond from '~/assets/images/currencies/Diamond Front.png';
import subscriptionHeader from '~/assets/images/season-pass/subscriptionHeaderBanner.png';

import styles from './NewView.module.scss';

export default function SubscriptionOverview({ ...props }) {
  const dispatch = useDispatch();

  const {
    ticketBalance,
    discordProfile,
    totalSubscriptionDiamondBonus = 0,
    subscriptionDiamondBonusToBeClaimed = 0,
  } = useSelector(userSelector);
  const {
    subscription: { validUntil, status },
  } = useFetchSelector(fetchActiveSubscriptionIfNeeded, subscriptionSelector);
  const { apiUrl } = useSelector(authSelector);
  const alert = useAlert();

  const isDiscordConnected = !!(discordProfile && discordProfile.id);
  const rewardClaimed = subscriptionDiamondBonusToBeClaimed > 0;
  const dateFormat = useDateFormat();
  const subscriptionValidUntilFormatted = validUntil
    ? format(new Date(validUntil), dateFormat)
    : '--';

  const [discordUrl, setDiscordUrl] = useState(null);
  const [showSuccessDialog, toggleShowSuccessDialog] = useBooleanState(false);
  const [scrollPos, setScrollPos] = useState(0);

  useEffect(() => {
    const searchString = props.location.search;
    const query = querystringify.parse(searchString);

    (async () => {
      try {
        const isSuccessPath = props.match.path === SEASON_TICKET_SUCCESS_PATH;
        const updatedUser = await dispatch(fetchUserIfNeeded(isSuccessPath));
        if (!evalIsUserSubscribed(updatedUser)) {
          props.history.replace(SEASON_TICKET_PATH);
        } else if (isSuccessPath) {
          toggleShowSuccessDialog();
        }
      } catch (e) {
        // empty
      }
    })();

    if (query && query.code) {
      dispatch(connectWithDiscord(query.code))
        .then(() =>
          alert.success(
            <AlertComponent
              body={I18n.t('subscription_summary_link_success')}
              title=""
            />,
          ),
        )
        .catch((err) =>
          alert.error(
            <AlertComponent
              body={err.message}
              title={I18n.t('common_error')}
            />,
          ),
        );
    } else if (apiUrl) {
      getDiscordAuthCodeUrl(apiUrl)
        .then((res) => setDiscordUrl(res.authorizationUrl))
        .catch(() => {});
    }

    if (query && query.error_description) {
      alert.error(
        <AlertComponent
          body={query.error_description}
          title=""
        />,
      );
    }
  }, [
    alert,
    apiUrl,
    dispatch,
    props.history,
    props.location.search,
    props.match.path,
    toggleShowSuccessDialog,
  ]);

  useEffect(() => {
    const handleNavigation = (event) => setScrollPos(event.target.scrollTop);

    window.addEventListener('scroll', handleNavigation, true);

    return () => window.removeEventListener('scroll', handleNavigation, true);
  }, []);

  return (
    <PopupClassic
      Button={SubscriptionCloseButton}
      dismissButtonClassName={styles.closeButton}
      effectsClassName={effectsClassNames.BOTTOM_TRANSITION}
      containerClassName={classnames(styles.subscriptionOverviewContainer)}
      className={styles.subscriptionOverview}
      wrapperClassName={styles.popupWrapper}
    >
      <img
        alt="LootBoy Season Ticket subscription page header"
        className={styles.headerImg}
        src={subscriptionHeader}
      />
      <div className={styles.body}>
        <h1
          className={classnames(
            styles.mainHeader,
            scrollPos > 0 && styles.hiddenElementMobile,
          )}
        >
          {I18n.t('subscription_summary_season_tickets')}
        </h1>
        <SectionTitle
          title={I18n.t('subscription_summary_season_ticket_boost')}
        />
        <SectionInfo
          title={<Title text={I18n.t('subscription_summary_ticket_balance')} />}
          content={
            <Content
              icon={ticket}
              text={ticketBalance}
            />
          }
        />
        <SectionInfo
          title={<Title text={I18n.t('subscription_summary_ticket_next')} />}
          content={<Content text={subscriptionValidUntilFormatted} />}
        />
        <SectionTitle title={I18n.t('subscription_summary_stash')} />
        <SectionInfo
          title={<Title text={I18n.t('subscription_summary_stash_balance')} />}
          content={
            <Content
              icon={diamond}
              text={subscriptionDiamondBonusToBeClaimed}
            />
          }
        />
        <span className={styles.legend}>
          {I18n.t('subscription_summary_stash_amount', {
            diamonds: totalSubscriptionDiamondBonus,
          })}
        </span>
        <ButtonPlainPromised
          className={styles.claim}
          disabled={!rewardClaimed}
          onClick={() => {
            dispatch(claimMonthlyDiamondBonus())
              .then(() =>
                alert.success(
                  <AlertComponent
                    body={I18n.t('subscription_summary_diamonds_claimed')}
                    title=""
                  />,
                ),
              )
              .catch((error) =>
                alert.error(
                  <AlertComponent
                    body={error.message}
                    title={I18n.t('common_error')}
                  />,
                ),
              );
          }}
        >
          {rewardClaimed
            ? I18n.t('subscription_summary_claim')
            : I18n.t('subscription_summary_already_claimed')}
        </ButtonPlainPromised>
        <PopupsSection
          discordUrl={discordUrl}
          isDiscordConnected={isDiscordConnected}
          showSuccessDialog={showSuccessDialog}
          subscriptionStatus={status}
          subscriptionValidUntil={subscriptionValidUntilFormatted}
          toggleShowSuccessDialog={toggleShowSuccessDialog}
        />
      </div>
    </PopupClassic>
  );
}

SubscriptionOverview.propTypes = {
  location: object.isRequired,
  match: object.isRequired,
  history: object.isRequired,
};
